import React, { useEffect, useRef } from 'react';

import { Tooltip } from 'antd';
import MergeClassNames from 'utils/MergeClassNames';

import { StepSelectorGroup } from './stepConfig';

interface CategoryTabStripProps {
  groups: StepSelectorGroup[];
  activeCategoryKey: string;
  /** Checked items per tab; counts show only while `showCheckedCounts` is on. */
  checkedCountByKey: Record<string, number>;
  showCheckedCounts: boolean;
  /** When set, every tab except the active one is disabled with this tooltip. */
  lockedCategoryReason?: string;
  /** Tabs disabled on their own, keyed by category, each with its reason. */
  lockedCategoryReasons?: Record<string, string>;
  onCategoryChange: (group: StepSelectorGroup) => void;
}

/** Step picker category tabs; keeps the active tab scrolled into view. */
const CategoryTabStrip: React.FC<CategoryTabStripProps> = ({
  groups,
  activeCategoryKey,
  checkedCountByKey,
  showCheckedCounts,
  lockedCategoryReason,
  lockedCategoryReasons,
  onCategoryChange,
}) => {
  const stripRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      const strip = stripRef.current;
      const tab = strip?.querySelector<HTMLElement>(
        `[data-category-key="${activeCategoryKey}"]`
      );
      if (!strip || !tab) return;
      const tabRight = tab.offsetLeft + tab.offsetWidth;
      if (tab.offsetLeft < strip.scrollLeft) {
        strip.scrollLeft = tab.offsetLeft;
      } else if (tabRight > strip.scrollLeft + strip.clientWidth) {
        strip.scrollLeft = tabRight - strip.clientWidth;
      }
    });
    return () => cancelAnimationFrame(frame);
  }, [activeCategoryKey]);

  return (
    <div
      ref={stripRef}
      className="step-selector-tab-strip mt-3 overflow-x-auto pb-1"
    >
      <div className="relative flex w-max gap-2">
        {groups.map((group) => {
          const isActive = group.key === activeCategoryKey;
          const checkedCount = checkedCountByKey[group.key] ?? 0;
          const lockReason =
            (!isActive && lockedCategoryReason) ||
            lockedCategoryReasons?.[group.key];
          const isLocked = !!lockReason;
          return (
            <Tooltip key={group.key} title={isLocked ? lockReason : null}>
              {/* span: antd Tooltip gets no mouse events from a pointer-events-none button */}
              <span
                data-category-key={group.key}
                className={
                  isLocked ? 'inline-flex cursor-not-allowed' : 'inline-flex'
                }
              >
                <button
                  type="button"
                  disabled={isLocked}
                  className={MergeClassNames(
                    'rounded-[var(--radius-button)] border px-3 py-1.5 text-sm font-medium',
                    isActive
                      ? 'border-charcoal bg-charcoal text-white'
                      : checkedCount > 0
                        ? 'border-charcoal bg-white text-charcoal'
                        : 'border-gray-medium bg-white text-charcoal',
                    isLocked && 'pointer-events-none opacity-50'
                  )}
                  onClick={() => onCategoryChange(group)}
                >
                  {group.label}
                  {showCheckedCounts &&
                    checkedCount > 0 &&
                    ` (${checkedCount})`}
                </button>
              </span>
            </Tooltip>
          );
        })}
      </div>
    </div>
  );
};

export default CategoryTabStrip;
